import type { QualityGrade } from "@cloudox/api-client";

/**
 * Knowledge Quality (KQI) grade badge. KQI measures *usefulness per audience*,
 * not correctness — so it is shown alongside views, never as a pass/fail gate.
 *
 * A `null` grade renders "Not scored" rather than a letter. The backend
 * withholds a grade when the assurance run did not cover everything it was
 * meant to, and a badge that fills that in has converted "we did not finish
 * measuring" into a verdict about the environment.
 */
export interface QualityBadgeProps {
  readonly grade: QualityGrade | null;
  readonly score?: number | null;
  /** Why there is no grade. Shown as the badge's tooltip when `grade` is null. */
  readonly unscoredReason?: string;
  readonly size?: "sm" | "md";
  readonly className?: string;
}

const GRADE_TONE: Record<QualityGrade, string> = {
  A: "var(--success)",
  B: "var(--success)",
  C: "var(--warning)",
  D: "var(--warning)",
  F: "var(--danger)",
};

const UNSCORED_LABEL = "Not scored";

export function QualityBadge({
  grade,
  score,
  unscoredReason,
  size = "md",
  className = "",
}: QualityBadgeProps) {
  const dim = size === "sm" ? 18 : 22;
  const fontSize = size === "sm" ? "0.7rem" : "0.8rem";

  if (grade === null) {
    const title = unscoredReason
      ? `Knowledge quality not scored — ${unscoredReason}`
      : "Knowledge quality not scored for this run";
    return (
      <span
        className={`inline-flex items-center gap-1.5 ${className}`.trim()}
        title={title}
      >
        <span
          className="inline-flex items-center rounded-md px-1.5 font-medium"
          style={{
            height: dim,
            fontSize,
            color: "var(--fg-muted)",
            backgroundColor:
              "color-mix(in oklab, var(--fg-muted) 12%, transparent)",
          }}
        >
          {UNSCORED_LABEL}
        </span>
        <span className="sr-only">{title}</span>
      </span>
    );
  }

  const color = GRADE_TONE[grade];
  return (
    <span
      className={`inline-flex items-center gap-1.5 ${className}`.trim()}
      title={scoreTitle(grade, score)}
    >
      <span
        className="inline-flex items-center justify-center rounded-md font-semibold"
        style={{
          width: dim,
          height: dim,
          fontSize,
          color,
          backgroundColor: `color-mix(in oklab, ${color} 16%, transparent)`,
        }}
      >
        {grade}
      </span>
      {typeof score === "number" ? (
        <span
          className="text-xs tabular-nums"
          style={{ color: "var(--fg-muted)" }}
        >
          {Math.round(score)}
        </span>
      ) : null}
      <span className="sr-only">Knowledge quality grade {grade}</span>
    </span>
  );
}

function scoreTitle(grade: QualityGrade, score?: number | null): string {
  return typeof score === "number"
    ? `Knowledge quality: grade ${grade} (${Math.round(score)}/100)`
    : `Knowledge quality: grade ${grade}`;
}
